import { type ContextShape } from '@ardoq/data-model';
import { type GraphItem, isCollapsibleGroup } from '@ardoq/graph';
import {
  collectRoutines,
  dispatchAction,
  extractPayload,
  ofType,
  routine,
} from '@ardoq/rxbeach';
import {
  GraphComponent,
  ILabel,
  type IModelItem,
  type INode,
} from '@ardoq/yfiles';
import { tap, withLatestFrom } from 'rxjs';
import { createReferences$ } from 'createReferences2024/createReferences$';
import { createReferenceLinks } from 'createReferences2024/createReferencesActions';
import { type CreateReferencesState } from 'createReferences2024/createReferencesTypes';
import type { GraphItem as OldGraphItem } from 'graph/GraphItem';
import { context$ } from 'streams/context/context$';
import { openSingleUrlFieldValue } from 'tabview/graphViews/openSingleUrlFieldValue';
import { relationshipDiagramToggleCollapseGroup } from 'tabview/relationshipDiagrams/actions';
import { ArdoqAddToScenario } from 'yfilesExtensions/styles/ArdoqAddToScenario';
import { ArdoqURLLabelStyle } from 'yfilesExtensions/styles/ardoqURLLabelStyle';
import { includeComponentIntoActiveScenario } from 'yfilesExtensions/styles/includeComponentIntoActiveScenario';
import { UrlLabelStyle } from 'yfilesExtensions/styles/UrlLabelStyle';
import { getLabelOwnerUrlFieldValues } from 'yfilesExtensions/styles/util';
import { WorkspaceModeOpenGroupUrlLabelStyle } from 'yfilesExtensions/styles/WorkspaceModeOpenGroupUrlLabelStyle';
import { dataModelId } from '../graphComponentUtil';
import { type GraphItemClickedRequest, graphItemClicked } from './actions';
import { modelItemOwner } from './graphItemClickedListenerModernized';
import { hitTestExpander } from './hitTestExpander';

type GraphItemClickedInputs = [
  GraphItemClickedRequest,
  CreateReferencesState,
  ContextShape,
];

const isUrlButton = (item: IModelItem): boolean =>
  item instanceof ILabel &&
  (item.style === UrlLabelStyle.Instance ||
    item.style === ArdoqURLLabelStyle.Instance ||
    item.style === WorkspaceModeOpenGroupUrlLabelStyle.Instance);

const isIncludeIntoScenarioButton = (item: IModelItem): boolean =>
  item instanceof ILabel && item.style instanceof ArdoqAddToScenario;

const respondToGraphItemClick = ([
  { event, viewId, viewInstanceId },
  { isLinking, linkSourceIds, refDirection },
  { componentId: contextComponentId },
]: GraphItemClickedInputs): void => {
  const { context, location, item } = event;
  const owner = modelItemOwner(item);
  if (!owner) {
    return;
  }
  const { tag } = owner;
  const graphItem = tag as GraphItem | OldGraphItem;
  if (!graphItem) {
    return;
  }

  const { id } = graphItem;
  const modelId = dataModelId(graphItem);
  if (isLinking) {
    dispatchAction(
      createReferenceLinks({
        linkSourcesIds: linkSourceIds,
        refDirection,
        linkTargetId: modelId,
      })
    );
    return;
  }

  if (
    viewId &&
    isCollapsibleGroup(graphItem) &&
    hitTestExpander({
      node: owner as INode,
      location,
      viewId,
      contextComponentId,
    })
  ) {
    dispatchAction(
      relationshipDiagramToggleCollapseGroup({
        viewId,
        groupId: id,
      }),
      viewInstanceId
    );
    event.handled = true;
    return;
  }

  if (isUrlButton(item)) {
    const { canvasComponent } = context;
    if (!(canvasComponent instanceof GraphComponent)) {
      return;
    }

    const { htmlElement } = canvasComponent;

    const urlFieldValues = getLabelOwnerUrlFieldValues(owner);
    if (urlFieldValues?.length) {
      openSingleUrlFieldValue(urlFieldValues, htmlElement);
    }
    event.handled = true;
    return;
  }

  if (isIncludeIntoScenarioButton(item)) {
    includeComponentIntoActiveScenario(modelId);
    event.handled = true;
  }
};

const handleGraphItemClicked = routine(
  ofType(graphItemClicked),
  extractPayload(),
  withLatestFrom(createReferences$, context$),
  tap(respondToGraphItemClick)
);

export const graphItemClickedRoutines = collectRoutines(handleGraphItemClicked);
